import { create } from 'zustand';
import type { Theme } from '@sokdaq/protocol';
import { platform, type DesktopState } from '../platform/bridge.js';

/**
 * 뷰 전환과 창 모드.
 *
 * 채팅 상태(메시지·멤버·프레즌스)와 분리해 둔다. 세 뷰는 같은 채팅
 * 스토어를 구독하고 표현만 달리하므로, 뷰를 바꿔도 연결이나 데이터에
 * 영향이 없어야 한다.
 */

const VIEW_STORAGE_KEY = 'sokdaq.view';

export interface UiState {
  view: Theme;
  /** 보스키로 스텔스에 들어오기 직전의 뷰. 토글로 돌아갈 때 쓴다. */
  viewBeforeStealth: Theme | null;
  alwaysOnTop: boolean;
  miniMode: boolean;
  autoStart: boolean;
  /** 안 읽은 메시지 수. 제목과 배지가 함께 읽는다. */
  unread: number;
  /** 설치된 앱 버전. 웹에서는 null. */
  appVersion: string | null;
  /** 내려받아 둔 새 버전. 없으면 null. */
  updateReady: string | null;
  /** 전역 보스키가 등록됐는지. 실패하면 창에 포커스가 있을 때만 동작한다. */
  bossKeyGlobal: boolean;

  setUnread: (count: number) => void;
  setUpdateReady: (version: string) => void;
  setView: (view: Theme) => void;
  toggleStealth: () => void;
  setAlwaysOnTop: (on: boolean) => void;
  setMiniMode: (on: boolean) => void;
  setAutoStart: (on: boolean) => void;
  /** 메인 프로세스가 들고 있는 값으로 맞춘다(시작 시, 트레이 조작 시). */
  syncDesktopState: (state: DesktopState) => void;
}

function loadInitialView(): Theme {
  try {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === 'office' || saved === 'stealth' || saved === 'classic') return saved;
  } catch {
    /* 스토리지를 못 읽어도 기본값으로 뜨면 된다. */
  }
  // 오피스 뷰가 실제로 돌아가는 M2부터 기본값을 'office'로 바꾼다.
  // 지금 오피스로 띄우면 처음 켠 사람이 빈 안내 화면부터 마주친다.
  return 'classic';
}

function persistView(view: Theme): void {
  try {
    // 스텔스를 기억해 두면 다음 실행이 위장 상태로 시작한다.
    // 의도한 동작이다 — 보스키를 누른 채 껐다면 그 상태가 맞다.
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    /* 무시 */
  }
}

export const useUiStore = create<UiState>((set, get) => ({
  view: loadInitialView(),
  viewBeforeStealth: null,
  alwaysOnTop: false,
  miniMode: false,
  autoStart: false,
  unread: 0,
  appVersion: null,
  updateReady: null,
  bossKeyGlobal: false,

  setUnread: (count) => set({ unread: Math.max(0, count) }),
  setUpdateReady: (version) => set({ updateReady: version }),

  setView: (view) => {
    // 미니 모드는 폭 320px다. 오피스 맵은 다섯 타일밖에 안 보여서 쓸 수 없다.
    if (get().miniMode && view === 'office') return;

    persistView(view);
    set({ view, viewBeforeStealth: null });
  },

  toggleStealth: () => {
    const { view, viewBeforeStealth } = get();

    if (view === 'stealth') {
      const restored = viewBeforeStealth ?? 'classic';
      persistView(restored);
      set({ view: restored, viewBeforeStealth: null });
      return;
    }

    persistView('stealth');
    set({ view: 'stealth', viewBeforeStealth: view });
  },

  setAlwaysOnTop: (on) => {
    platform.setAlwaysOnTop(on);
    set({ alwaysOnTop: on });
  },

  setMiniMode: (on) => {
    platform.setMiniMode(on);

    // 좁은 창에서 오피스 맵은 볼 수 없다. 클래식으로 옮겨준다.
    const { view } = get();
    if (on && view === 'office') {
      persistView('classic');
      set({ miniMode: on, view: 'classic', viewBeforeStealth: null });
      return;
    }
    set({ miniMode: on });
  },

  setAutoStart: (on) => {
    platform.setAutoStart(on);
    set({ autoStart: on });
  },

  syncDesktopState: (state) => {
    const { view } = get();
    const needsViewChange = state.miniMode && view === 'office';
    if (needsViewChange) persistView('classic');

    set({
      alwaysOnTop: state.alwaysOnTop,
      miniMode: state.miniMode,
      autoStart: state.autoStart,
      appVersion: state.appVersion,
      updateReady: state.updateReady,
      bossKeyGlobal: state.bossKeyGlobal,
      ...(needsViewChange ? { view: 'classic' as Theme, viewBeforeStealth: null } : {}),
    });
  },
}));
